import { z } from 'zod'
import { cardanoTxHashSchema } from './source'

/** API の整数数量。Number へ変換せず精度を保持する。 */
export const cardanoQuantitySchema = z.string().regex(/^(0|[1-9][0-9]*)$/)
/** Policy ID と hexadecimal Asset Name からなる Native Asset 識別子。 */
export const cardanoNativeUnitSchema = z
  .string()
  .regex(/^[0-9a-f]{56}([0-9a-f]{2}){0,32}$/)
/** lovelace と Native Asset の入力・出力数量。 */
export const cardanoAmountSchema = z.object({
  unit: z.union([z.literal('lovelace'), cardanoNativeUnitSchema]),
  quantity: cardanoQuantitySchema,
})
/** UTxO の金額と所有者。Script の取引相手も文字列として保持する。 */
const utxoSchema = z.object({
  address: z.string(),
  amount: z.array(cardanoAmountSchema),
  output_index: z.number().int().nonnegative(),
  collateral: z.boolean(),
})
/** Reference Input と担保を区別する実行時 UTxO schema。 */
export const cardanoUtxosSchema = z.object({
  hash: cardanoTxHashSchema,
  inputs: z.array(
    utxoSchema.extend({
      tx_hash: cardanoTxHashSchema,
      reference: z.boolean().optional().default(false),
    }),
  ),
  outputs: z.array(utxoSchema),
})
/** Tx の有効性・実日時・手数料・保証金を検証する。 */
export const cardanoTransactionSchema = z.object({
  hash: cardanoTxHashSchema,
  block_time: z.number().int().nonnegative(),
  block_height: z.number().int().nonnegative(),
  slot: z.number().int().nonnegative().safe(),
  fees: cardanoQuantitySchema,
  deposit: z.string().regex(/^-?(0|[1-9][0-9]*)$/),
  valid_contract: z.boolean(),
  withdrawal_count: z.number().int().nonnegative(),
  stake_cert_count: z.number().int().nonnegative(),
  pool_update_count: z.number().int().nonnegative(),
  pool_retire_count: z.number().int().nonnegative(),
})
/** アドレスの Tx 索引。時刻は API の UNIX 秒。 */
export const cardanoTransactionIndexSchema = z.object({
  tx_hash: cardanoTxHashSchema,
  block_time: z.number().int().nonnegative(),
  block_height: z.number().int().nonnegative(),
  tx_index: z.number().int().nonnegative(),
})
/** Stake 関連 Payment Address 探索結果。 */
export const cardanoPaymentAddressSchema = z.object({ address: z.string() })
/** 報酬源別レコード。epoch は earned epoch であり利用可能 epoch と区別する。 */
export const cardanoRewardSchema = z.object({
  epoch: z.number().int().nonnegative(),
  amount: cardanoQuantitySchema,
  pool_id: z.string(),
  type: z.enum(['leader', 'member', 'pool_deposit_refund']),
})
/** Tx 内の Reward Account からの引出し。 */
export const cardanoWithdrawalSchema = z.object({
  address: z.string(),
  amount: cardanoQuantitySchema,
})
/** Stake Key 登録・解除の証書。 */
export const cardanoStakeCertificateSchema = z.object({
  cert_index: z.number().int().nonnegative(),
  address: z.string(),
  registration: z.boolean(),
})
/** 過去の登録保証金。解除時には最後の登録の実額を参照する。 */
export const cardanoRegistrationSchema = z.object({
  tx_hash: cardanoTxHashSchema,
  action: z.enum(['registered', 'deregistered']),
  deposit: cardanoQuantitySchema.nullable(),
  block_time: z.number().int().nonnegative(),
  tx_slot: z.number().int().nonnegative(),
})
/** Epoch の開始時刻と番号を照合する。 */
export const cardanoEpochSchema = z.object({
  epoch: z.number().int().nonnegative(),
  start_time: z.number().int().nonnegative(),
})
/** Native Asset の Token Registry metadata。NFT 専用 metadata は利用しない。 */
export const cardanoAssetMetadataSchema = z.object({
  asset: cardanoNativeUnitSchema,
  metadata: z
    .object({
      name: z.string(),
      ticker: z.string().nullable().optional(),
      decimals: z.number().int().min(0).max(255).nullable().optional(),
    })
    .nullable(),
})
/** 検証済み Tx。 */
export type CardanoTransaction = z.infer<typeof cardanoTransactionSchema>
/** 検証済み UTxO。 */
export type CardanoUtxos = z.infer<typeof cardanoUtxosSchema>
